import { useState, useEffect, lazy, Suspense } from 'react'
import { HashRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom'
import { useStore } from './store/useStore'
import { ErrorBoundary } from './components/ErrorBoundary'
import SplashPage from './components/SplashPage'
import BackgroundMusic from './components/BackgroundMusic'
import type { Letter } from './types'

const LetterWall = lazy(() => import('./components/LetterWall'))
const AboutPage = lazy(() => import('./components/AboutPage'))
const JourneyPage = lazy(() => import('./components/JourneyPage'))
const MemoryBoxPage = lazy(() => import('./components/MemoryBoxPage'))
const LetterDetail = lazy(() => import('./components/LetterDetail'))
const LetterGenerator = lazy(() => import('./components/LetterGenerator'))

function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-8 h-8 border-2 border-sepia-400 border-t-sepia-800 rounded-full animate-spin" />
    </div>
  )
}

function OverlayFallback() {
  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-md flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-sepia-400 border-t-sepia-800 rounded-full animate-spin" />
    </div>
  )
}

function AppContent() {
  const language = useStore(state => state.language)
  const script = useStore(state => state.script)
  const showPinyin = useStore(state => state.showPinyin)
  const error = useStore(state => state.error)
  const setLanguage = useStore(state => state.setLanguage)
  const setScript = useStore(state => state.setScript)
  const togglePinyin = useStore(state => state.togglePinyin)
  const fetchLeaderboard = useStore(state => state.fetchLeaderboard)
  const fetchLetter = useStore(state => state.fetchLetter)

  const [showSplash, setShowSplash] = useState(false)
  const [showGenerator, setShowGenerator] = useState(false)
  const [selectedLetter, setSelectedLetter] = useState<Letter | null>(null)

  useEffect(() => {
    if (localStorage.getItem('dear-you-splash-seen') !== 'true') {
      setShowSplash(true)
    }
  }, [])

  // fetchLeaderboard is a stable zustand action, so this effect runs exactly once.
  useEffect(() => {
    fetchLeaderboard()
    const interval = setInterval(() => fetchLeaderboard(), 60 * 60 * 1000)
    return () => clearInterval(interval)
  }, [fetchLeaderboard])

  // Deep link: /?ref=<letter-id> opens that letter (used by the share QR code).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const ref = params.get('ref')
    if (!ref) return
    let cancelled = false
    fetchLetter(ref).then(letter => {
      if (cancelled || !letter) return
      setSelectedLetter(letter)
      params.delete('ref')
      const search = params.toString()
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`,
      )
    })
    return () => { cancelled = true }
  }, [fetchLetter])

  const t = language === 'zh-CN'

  return (
    <div className="min-h-screen bg-desk text-sepia-800 relative selection:bg-cinnabar/20">
      {/* Light flickers simulation overlay */}
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-[#ffd700]/[0.015] via-transparent to-transparent animate-lamp-flicker z-10" />

      <a href="#main-content" className="skip-link">
        {t ? '跳到主要内容' : 'Skip to main content'}
      </a>
      {showSplash && (
        <SplashPage onEnter={() => {
          setShowSplash(false)
          localStorage.setItem('dear-you-splash-seen', 'true')
        }} />
      )}
      {/* Navigation */}
      <nav className="fixed top-4 left-4 right-4 md:left-8 md:right-8 z-50 flex items-center justify-between px-6 py-3 bg-sepia-900/60 backdrop-blur-md border border-sepia-800/40 rounded-xl shadow-deep text-sepia-200 transition-all duration-300" aria-label={t ? '主导航' : 'Main navigation'}>
        <div className="flex items-center gap-4">
          <Link to="/" className="font-handwriting text-2xl text-white hover:text-amber-200 transition-colors tracking-wider">
            Dear You
          </Link>
        </div>

        <div className="flex items-center gap-3 md:gap-5">
          <Link to="/" className="text-sm font-serif text-sepia-300 hover:text-white transition-colors">
            {t ? '信件墙' : 'Wall'}
          </Link>
          <Link to="/about" className="text-sm font-serif text-sepia-300 hover:text-white transition-colors hidden md:block">
            {t ? '关于' : 'About'}
          </Link>
          <Link to="/journey" className="text-sm font-serif text-sepia-300 hover:text-white transition-colors hidden lg:block">
            {t ? '时光长廊' : 'Journey'}
          </Link>
          <Link to="/memory-box" className="text-sm font-serif text-sepia-300 hover:text-white transition-colors hidden lg:block">
            {t ? '记忆抽屉' : 'Memory Box'}
          </Link>

          {/* Settings Segmented Control */}
          <div className="hidden sm:flex bg-[#1a120b] rounded-lg border border-sepia-800/60 p-0.5 shadow-inner">
            <button
              type="button"
              onClick={() => setLanguage(language === 'zh-CN' ? 'en' : 'zh-CN')}
              className="text-xs font-serif px-2.5 py-1 rounded-md transition-all duration-200 ease-spring text-sepia-400 hover:text-white active:scale-95"
              aria-label="Toggle language"
            >
              {language === 'zh-CN' ? 'EN' : '中'}
            </button>
            <div className="w-px bg-sepia-800/60 mx-0.5 my-1" />
            <button
              type="button"
              onClick={() => setScript(script === 'traditional' ? 'simplified' : 'traditional')}
              className="text-xs font-serif px-2.5 py-1 rounded-md transition-all duration-200 ease-spring text-sepia-400 hover:text-white active:scale-95"
              aria-label="Toggle script"
            >
              {script === 'traditional' ? '简' : '繁'}
            </button>
            <div className="w-px bg-sepia-800/60 mx-0.5 my-1" />
            <button
              type="button"
              onClick={togglePinyin}
              className={`text-xs font-serif px-2.5 py-1 rounded-md transition-all duration-200 ease-spring active:scale-95 ${
                showPinyin ? 'bg-sepia-700/80 text-amber-200 shadow-sm' : 'text-sepia-400 hover:text-white'
              }`}
              aria-label="Toggle pinyin"
            >
              拼
            </button>
          </div>

          {/* Background music */}
          <div className="hidden sm:block">
            <BackgroundMusic />
          </div>

          {/* Write CTA */}
          <button
            type="button"
            onClick={() => setShowGenerator(true)}
            className="text-sm font-serif text-white bg-cinnabar hover:bg-seal-red-dark px-5 py-1.5 rounded-full font-bold shadow-[0_4px_14px_rgba(176,37,21,0.25)] hover:shadow-[0_6px_20px_rgba(176,37,21,0.35)] transition-all duration-300 ease-spring active:scale-95"
          >
            {t ? '写一封信' : 'Write'}
          </button>
        </div>
      </nav>

      {/* Main content wrapper with dynamic depth scaling */}
      <div className={`transition-all duration-500 ease-spring ${showGenerator || selectedLetter ? 'scale-[0.97] opacity-60 pointer-events-none blur-[1px]' : 'scale-100 opacity-100 blur-0'}`}>
        <main id="main-content" className="pt-24 min-h-[85vh]">
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/" element={<LetterWall onLetterClick={setSelectedLetter} />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/journey" element={<JourneyPage />} />
            <Route path="/memory-box" element={<MemoryBoxPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>

      {/* Footer */}
      <footer className="py-12 text-center text-sepia-500 text-sm font-serif border-t border-sepia-900/40">
        <p className="tracking-wide">Dear You — 给阿嬷的情书</p>
        <Link to="/about" className="hover:text-sepia-300 transition-colors mt-2 inline-block">
          {t ? '关于' : 'About'} · {t ? '侨批文化' : 'Qiaopi Heritage'}
        </Link>
      </footer>
      </div>

      {/* Letter detail overlay */}
      {selectedLetter && (
        <ErrorBoundary>
          <Suspense fallback={<OverlayFallback />}>
            <LetterDetail letter={selectedLetter} onClose={() => setSelectedLetter(null)} />
          </Suspense>
        </ErrorBoundary>
      )}

      {/* Letter generator modal */}
      {showGenerator && (
        <ErrorBoundary>
          <Suspense fallback={<OverlayFallback />}>
            <LetterGenerator onClose={() => setShowGenerator(false)} />
          </Suspense>
        </ErrorBoundary>
      )}

      {/* Live region for dynamic updates */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {error || ''}
      </div>
    </div>
  )
}

export default function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AppContent />
      </Router>
    </ErrorBoundary>
  )
}
